/**
 * The grace-period control shared by the rotate and retire key dialogs:
 * five presets plus a bounded custom field, in `[0, 604800]` seconds (core
 * `db/models/commercial/keys.py:34`, `MAX_PARAMETER_GRACE_SECONDS`). Each
 * dialog owns its own legend text and default/no-default starting choice;
 * everything else about the control -- the bound, the presets, and how a
 * typed value is validated -- is one definition.
 */

import type { ReactNode } from 'react';

import { DurationInput } from '../../chrome/form';
import { defineCatalogue } from '../../i18n';
import {
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
  FieldLegend,
  FieldSet,
} from '../../ui/field';
import { Label } from '../../ui/label';
import { RadioGroup, RadioGroupItem } from '../../ui/radio-group';

export type GraceChoice =
  'immediately' | 'oneHour' | 'oneDay' | 'threeDays' | 'sevenDays' | 'custom';

export const MAX_GRACE_SECONDS = 604800;

const GRACE_PRESETS: Record<Exclude<GraceChoice, 'custom'>, number> = {
  immediately: 0,
  oneHour: 3600,
  oneDay: 86400,
  threeDays: 259200,
  sevenDays: 604800,
};

export const GRACE_PRESET_ORDER: Exclude<GraceChoice, 'custom'>[] = [
  'immediately',
  'oneHour',
  'oneDay',
  'threeDays',
  'sevenDays',
];

/** `null` means no confirmable value: nothing picked yet, or `custom` picked
 *  with an empty or out-of-range field. Never a fallback number -- a caller
 *  that wants a default passes a non-null starting `choice` instead. */
export function graceSecondsFor(choice: GraceChoice | null, customValue: string): number | null {
  if (choice === null) return null;
  if (choice !== 'custom') return GRACE_PRESETS[choice];
  if (customValue.trim() === '') return null;
  const parsed = Number(customValue);
  if (!Number.isInteger(parsed) || parsed < 0 || parsed > MAX_GRACE_SECONDS) return null;
  return parsed;
}

type GraceKey =
  | 'grace.immediately'
  | 'grace.oneHour'
  | 'grace.oneDay'
  | 'grace.threeDays'
  | 'grace.sevenDays'
  | 'grace.custom'
  | 'grace.custom.label'
  | 'grace.custom.hint'
  | 'grace.custom.error';

const catalogue = defineCatalogue<Record<GraceKey, string>>(
  {
    'grace.immediately': 'Imediatamente',
    'grace.oneHour': '1 hora',
    'grace.oneDay': '24 horas',
    'grace.threeDays': '3 dias',
    'grace.sevenDays': '7 dias',
    'grace.custom': 'Personalizado',
    'grace.custom.label': 'Carência',
    'grace.custom.hint': 'Até 7 dias.',
    'grace.custom.error': 'Informe a carência, até 7 dias.',
  },
  {
    'grace.immediately': 'Immediately',
    'grace.oneHour': '1 hour',
    'grace.oneDay': '24 hours',
    'grace.threeDays': '3 days',
    'grace.sevenDays': '7 days',
    'grace.custom': 'Custom',
    'grace.custom.label': 'Grace period',
    'grace.custom.hint': 'Up to 7 days.',
    'grace.custom.error': 'Enter the grace period, up to 7 days.',
  },
);
const gt = catalogue.t;

/** `customInvalid` is computed by the caller, not here: the rotate dialog
 *  must flag an empty custom field as soon as a submit is attempted (no
 *  submit-disabled affordance to lean on), while the retire dialog's
 *  `ConfirmDialog` already disables its confirm button on the same
 *  condition and only needs the message once the field holds something
 *  invalid. Both callers derive `customInvalid` from `graceSecondsFor`. */
export function GracePeriodField(props: {
  idPrefix: string;
  legendId: string;
  legend: string;
  choice: GraceChoice | null;
  onChoiceChange: (choice: GraceChoice) => void;
  customValue: string;
  onCustomValueChange: (value: string) => void;
  customInvalid: boolean;
}): ReactNode {
  const customId = `${props.idPrefix}-custom`;
  return (
    <FieldSet>
      <FieldLegend variant="label" id={props.legendId}>
        {props.legend}
      </FieldLegend>
      <RadioGroup
        aria-labelledby={props.legendId}
        value={props.choice ?? ''}
        onValueChange={(value) => props.onChoiceChange(value as GraceChoice)}
      >
        {GRACE_PRESET_ORDER.map((preset) => (
          <div key={preset} className="flex items-center gap-2">
            <RadioGroupItem value={preset} id={`${props.idPrefix}-${preset}`} />
            <Label htmlFor={`${props.idPrefix}-${preset}`}>{gt(`grace.${preset}`)}</Label>
          </div>
        ))}
        <div className="flex items-center gap-2">
          <RadioGroupItem value="custom" id={`${props.idPrefix}-custom-radio`} />
          <Label htmlFor={`${props.idPrefix}-custom-radio`}>{gt('grace.custom')}</Label>
        </div>
      </RadioGroup>
      {props.choice === 'custom' && (
        <Field data-invalid={props.customInvalid ? true : undefined}>
          <FieldLabel htmlFor={customId}>{gt('grace.custom.label')}</FieldLabel>
          <DurationInput
            id={customId}
            seconds={props.customValue.trim() === '' ? null : Number(props.customValue)}
            onChange={(seconds) =>
              props.onCustomValueChange(seconds === null ? '' : String(seconds))
            }
            minSeconds={0}
            maxSeconds={MAX_GRACE_SECONDS}
            units={['minutes', 'hours', 'days']}
          />
          <FieldDescription>{gt('grace.custom.hint')}</FieldDescription>
          {props.customInvalid && props.customValue.trim() === '' && (
            <FieldError>{gt('grace.custom.error')}</FieldError>
          )}
        </Field>
      )}
    </FieldSet>
  );
}
